import React, { CSSProperties } from 'react';
import { SizeProp } from '../icon';
import { FaIcons } from '../fa-icons/FaIcons';

/**
 * {@docCategory Icons}
 */
export interface SimpleIconProps {
   /**
    * The icon size in fractional 'em' units.
    * @defaultvalue 1
    */
   size?: number | SizeProp;

   /**
    * Set the make the icon a fixed width
    * @defaultvalue false
    */
   fixedWidth?: boolean;

   /**
    * Invert the icon
    * @defaultvalue false
    */
   inverse?: boolean;

   /**
    * The icon set to use.
    * @defaultvalue light
    */
   iconSet?: 'light' | 'regular' | 'solid';

   /**
    * Sets a className.
    */
   className?: string;

   /**
    * Sets styles.
    */
   style?: CSSProperties;

   /**
    * Callback when clicked.
    */
   onClick?: (event: React.MouseEvent<SVGSVGElement>) => void;
}

export const Icons = {
   Add: (props: SimpleIconProps) => <FaIcons.Plus {...props}/>,
   Approve: (props: SimpleIconProps) => <FaIcons.Check {...props}/>,
   ApproveAll: (props: SimpleIconProps) => <FaIcons.CheckDouble {...props}/>,
   AssignEmployee: (props: SimpleIconProps) => <FaIcons.UserPlus {...props}/>,
   Back: (props: SimpleIconProps) => <FaIcons.SignInAlt {...props}/>,
   Ban: (props: SimpleIconProps) => <FaIcons.Ban {...props}/>,
   Bars: (props: SimpleIconProps) => <FaIcons.Bars {...props}/>,
   BarChart: (props: SimpleIconProps) => <FaIcons.ChartBar {...props}/>,
   Bell: (props: SimpleIconProps) => <FaIcons.Bell {...props}/>,
   Bookmark: (props: SimpleIconProps) => <FaIcons.Bookmark {...props} iconSet={'solid'}/>,
   Break: (props: SimpleIconProps) => <FaIcons.Coffee {...props}/>,
   Calculate: (props: SimpleIconProps) => <FaIcons.Clock {...props}/>,
   Calendar: (props: SimpleIconProps) => <FaIcons.Calendar {...props}/>,
   Cancel: (props: SimpleIconProps) => <FaIcons.Ban {...props}/>,
   ChangeDown: (props: SimpleIconProps) => <FaIcons.ArrowAltDown {...props}/>,
   ChangeLeft: (props: SimpleIconProps) => <FaIcons.ArrowAltLeft {...props}/>,
   ChangeRight: (props: SimpleIconProps) => <FaIcons.ArrowAltRight {...props}/>,
   ChangeUp: (props: SimpleIconProps) => <FaIcons.ArrowAltUp {...props}/>,
   Check: (props: SimpleIconProps) => <FaIcons.Check {...props}/>,
   ClockIn: (props: SimpleIconProps) => <FaIcons.UserPlus {...props}/>,
   ClockOut: (props: SimpleIconProps) => <FaIcons.UserTimes {...props}/>,
   Close: (props: SimpleIconProps) => <FaIcons.Times {...props}/>,
   CollapseAll: (props: SimpleIconProps) => <FaIcons.MinusSquare {...props}/>,
   Comment: (props: SimpleIconProps) => <FaIcons.Comment {...props}/>,
   Configure: (props: SimpleIconProps) => <FaIcons.Cogs {...props}/>,
   ContractWindow: (props: SimpleIconProps) => <FaIcons.Compress {...props}/>,
   Copy: (props: SimpleIconProps) => <FaIcons.Copy {...props}/>,
   CostConcern: (props: SimpleIconProps) => <FaIcons.ArrowCircleUp {...props}/>,
   Currency: (props: SimpleIconProps) => <FaIcons.MoneyBill {...props}/>,
   Cut: (props: SimpleIconProps) => <FaIcons.Cut {...props}/>,
   Database: (props: SimpleIconProps) => <FaIcons.Database {...props}/>,
   Date: (props: SimpleIconProps) => <FaIcons.Calendar {...props}/>,
   Delete: (props: SimpleIconProps) => <FaIcons.TrashAlt {...props}/>,
   Deny: (props: SimpleIconProps) => <FaIcons.Times {...props}/>,
   DeSelectAll: (props: SimpleIconProps) => <FaIcons.Square {...props}/>,
   Download: (props: SimpleIconProps) => <FaIcons.CloudDownload {...props}/>,
   DragHandles: (props: SimpleIconProps) => <FaIcons.GripLines {...props}/>,
   DragHandlesVertical: (props: SimpleIconProps) => <FaIcons.GripLinesVertical {...props}/>,
   Earnings: (props: SimpleIconProps) => <FaIcons.HandHoldingUsd {...props}/>,
   Edit: (props: SimpleIconProps) => <FaIcons.Edit {...props}/>,
   Ellipsis: (props: SimpleIconProps) => <FaIcons.EllipsisH {...props}/>,
   Email: (props: SimpleIconProps) => <FaIcons.At {...props}/>,
   Employee: (props: SimpleIconProps) => <FaIcons.User {...props}/>,
   Error: (props: SimpleIconProps) => <FaIcons.ExclamationTriangle {...props}/>,
   Exchange: (props: SimpleIconProps) => <FaIcons.Exchange {...props}/>,
   ExpandAll: (props: SimpleIconProps) => <FaIcons.PlusSquare {...props}/>,
   ExpandWindow: (props: SimpleIconProps) => <FaIcons.Expand {...props}/>,
   FBOutlet: (props: SimpleIconProps) => <FaIcons.UtensilsAlt {...props}/>,
   Favorite: (props: SimpleIconProps) => <FaIcons.Star {...props} iconSet={'solid'}/>,
   Feedback: (props: SimpleIconProps) => <FaIcons.QuoteRight {...props}/>,
   FileExport: (props: SimpleIconProps) => <FaIcons.FileExport {...props}/>,
   FileImport: (props: SimpleIconProps) => <FaIcons.FileImport {...props}/>,
   Filter: (props: SimpleIconProps) => <FaIcons.Filter {...props}/>,
   Forward: (props: SimpleIconProps) => <FaIcons.Share {...props}/>,
   Group: (props: SimpleIconProps) => <FaIcons.UsersClass {...props}/>,
   Help: (props: SimpleIconProps) => <FaIcons.QuestionCircle {...props}/>,
   History: (props: SimpleIconProps) => <FaIcons.History {...props}/>,
   Home: (props: SimpleIconProps) => <FaIcons.Home {...props}/>,
   HomeJob: (props: SimpleIconProps) => <FaIcons.Home {...props}/>,
   Hotel: (props: SimpleIconProps) => <FaIcons.Hotel {...props}/>,
   HotelRoom: (props: SimpleIconProps) => <FaIcons.BedAlt {...props}/>,
   Indent: (props: SimpleIconProps) => <FaIcons.Indent {...props}/>,
   InProgress: (props: SimpleIconProps) => <FaIcons.SpinnerThird {...props}/>,
   Information: (props: SimpleIconProps) => <FaIcons.InfoCircle {...props} className={`information-icon ${props.className}`}/>,
   JumpToEnd: (props: SimpleIconProps) => <FaIcons.ChevronDoubleRight {...props}/>,
   JumpToStart: (props: SimpleIconProps) => <FaIcons.ChevronDoubleLeft {...props}/>,
   LineChart: (props: SimpleIconProps) => <FaIcons.ChartLine {...props}/>,
   Manager: (props: SimpleIconProps) => <FaIcons.UserTie {...props}/>,
   ManualShift: (props: SimpleIconProps) => <FaIcons.UserTie {...props}/>,
   Map: (props: SimpleIconProps) => <FaIcons.Map {...props}/>,
   Message: (props: SimpleIconProps) => <FaIcons.Envelope {...props}/>,
   MoreOptions: (props: SimpleIconProps) => <FaIcons.ChevronCircleDown {...props}/>,
   MostUsed: (props: SimpleIconProps) => <FaIcons.Heart {...props}/>,
   MoveDown: (props: SimpleIconProps) => <FaIcons.ArrowAltDown {...props}/>,
   MoveUp: (props: SimpleIconProps) => <FaIcons.ArrowAltUp {...props}/>,
   Next: (props: SimpleIconProps) => <FaIcons.ChevronRight {...props}/>,
   Note: (props: SimpleIconProps) => <FaIcons.StickyNote {...props}/>,
   Notification: (props: SimpleIconProps) => <FaIcons.Bell {...props}/>,
   OpenExternalLink: (props: SimpleIconProps) => <FaIcons.ExternalLink {...props}/>,
   Options: (props: SimpleIconProps) => <FaIcons.Cog {...props}/>,
   Outdent: (props: SimpleIconProps) => <FaIcons.Outdent {...props}/>,
   Paste: (props: SimpleIconProps) => <FaIcons.Paste {...props}/>,
   Pending: (props: SimpleIconProps) => <FaIcons.HourglassStart {...props}/>,
   PhoneNumber: (props: SimpleIconProps) => <FaIcons.Mobile {...props}/>,
   Previous: (props: SimpleIconProps) => <FaIcons.ChevronLeft {...props}/>,
   Print: (props: SimpleIconProps) => <FaIcons.Print {...props}/>,
   Projection: (props: SimpleIconProps) => <FaIcons.Binoculars {...props}/>,
   QualityConcern: (props: SimpleIconProps) => <FaIcons.ArrowCircleDown {...props}/>,
   Redo: (props: SimpleIconProps) => <FaIcons.Redo {...props}/>,
   Remove: (props: SimpleIconProps) => <FaIcons.Ban {...props}/>,
   Reply: (props: SimpleIconProps) => <FaIcons.Reply {...props}/>,
   RunProcess: (props: SimpleIconProps) => <FaIcons.Bolt {...props}/>,
   Save: (props: SimpleIconProps) => <FaIcons.Save {...props}/>,
   Search: (props: SimpleIconProps) => <FaIcons.Search {...props}/>,
   SelectAll: (props: SimpleIconProps) => <FaIcons.CheckSquare {...props}/>,
   SentimentBad: (props: SimpleIconProps) => <FaIcons.Frown {...props}/>,
   SentimentGood: (props: SimpleIconProps) => <FaIcons.Smile {...props}/>,
   SentimentNeutral: (props: SimpleIconProps) => <FaIcons.Meh {...props}/>,
   ShowMore: (props: SimpleIconProps) => <FaIcons.EllipsisH {...props}/>,
   SignIn: (props: SimpleIconProps) => <FaIcons.SignIn {...props}/>,
   SignOut: (props: SimpleIconProps) => <FaIcons.SignOut {...props}/>,
   Sort: (props: SimpleIconProps) => <FaIcons.Sort {...props}/>,
   StatusActive: (props: SimpleIconProps) => <FaIcons.CheckCircle {...props}/>,
   StatusInactive: (props: SimpleIconProps) => <FaIcons.TimesCircle {...props}/>,
   Table: (props: SimpleIconProps) => <FaIcons.Table {...props}/>,
   Time: (props: SimpleIconProps) => <FaIcons.Clock {...props}/>,
   UnBookmark: (props: SimpleIconProps) => <FaIcons.Bookmark {...props}/>,
   Undo: (props: SimpleIconProps) => <FaIcons.Undo {...props}/>,
   UnFavorite: (props: SimpleIconProps) => <FaIcons.Star {...props}/>,
   Upload: (props: SimpleIconProps) => <FaIcons.CloudUpload {...props}/>,
   User: (props: SimpleIconProps) => <FaIcons.UserCircle {...props}/>,
   UserClock: (props: SimpleIconProps) => <FaIcons.UserClock {...props}/>,
   UserFriends: (props: SimpleIconProps) => <FaIcons.UserFriends {...props}/>,
   UserGroup: (props: SimpleIconProps) => <FaIcons.UsersClass {...props}/>,
   UserProfile: (props: SimpleIconProps) => <FaIcons.UserCircle {...props}/>,
   ViewAsGallery: (props: SimpleIconProps) => <FaIcons.Images {...props}/>,
   ViewAsThumbnails: (props: SimpleIconProps) => <FaIcons.Table {...props}/>,
   VisibilityOff: (props: SimpleIconProps) => <FaIcons.EyeSlash {...props}/>,
   VisibilityOn: (props: SimpleIconProps) => <FaIcons.Eye {...props}/>,
   Warning: (props: SimpleIconProps) => <FaIcons.ExclamationCircle {...props}/>,
   Weather: (props: SimpleIconProps) => <FaIcons.CloudsSun {...props}/>,
};
